/* ————————————————————————————————————————————————
   Peace & Pure — motion engine
   Lenis + GSAP ScrollTrigger, with full graceful
   degradation: no JS, no WebGL, reduced motion,
   coarse pointers all get a complete site.
   ———————————————————————————————————————————————— */

// `?flat` forces the reduced-motion path — a static, natively-scrollable
// render with every element visible. Used for headless QA/screenshots
// (where smooth-scroll libraries and H.264 video misbehave) and honoured
// as an accessibility escape hatch.
const flatMode = location.search.includes('flat') || !!(window.Shopify && window.Shopify.designMode);
const reduceMotion = flatMode || matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = matchMedia('(pointer: fine)').matches;
const desktop = matchMedia('(min-width: 861px)').matches;
const hasGsap = typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined';

/* The .js class gates all hidden-until-reveal CSS. Only engage
   when the animation stack actually loaded. */
if (hasGsap && !reduceMotion) {
  document.documentElement.classList.add('js');
  gsap.registerPlugin(ScrollTrigger);
}

/* ————— Smooth scroll ————— */
let lenis = null;
if (hasGsap && !reduceMotion && typeof window.Lenis !== 'undefined' && finePointer) {
  lenis = new Lenis({ lerp: 0.12, wheelMultiplier: 1 });
  window.__lenis = lenis;
  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add((t) => lenis.raf(t * 1000));
  gsap.ticker.lagSmoothing(0);
  // anchor links through Lenis
  document.querySelectorAll('a[href^="#"]').forEach((a) => {
    a.addEventListener('click', (e) => {
      const target = document.querySelector(a.getAttribute('href'));
      if (!target) return;
      e.preventDefault();
      lenis.scrollTo(target, { offset: -20, duration: 1.4 });
    });
  });
}

/* ————— Nav chrome ————— */
const nav = document.querySelector('[data-nav]');
if (nav) {
  // Hysteresis on both states so smooth-scroll jitter around a threshold
  // can never rapid-toggle the classes (the "flickering menu" bug):
  // - pill engages past 90px, releases only below 30px
  // - hide only after 140px of accumulated downward travel, show after
  //   60px of accumulated upward travel
  let lastY = window.scrollY;
  let acc = 0;
  let ticking = false;
  const update = () => {
    ticking = false;
    const y = window.scrollY;
    const dy = y - lastY;
    lastY = y;
    if ((dy > 0) !== (acc > 0)) acc = 0; // direction change resets the accumulator
    acc += dy;

    // guard every write: classList.add/remove serialise the attribute even
    // when nothing changes, which spams style recalcs at 60fps under Lenis
    const scrolled = nav.classList.contains('is-scrolled');
    if (y > 90 && !scrolled) nav.classList.add('is-scrolled');
    else if (y < 30 && scrolled) nav.classList.remove('is-scrolled');

    const hidden = nav.classList.contains('is-hidden');
    const wantShow = document.body.classList.contains('menu-open') || y < 700 || acc < -60;
    if (wantShow && hidden) nav.classList.remove('is-hidden');
    else if (!wantShow && !hidden && acc > 140) nav.classList.add('is-hidden');
  };
  addEventListener('scroll', () => {
    if (!ticking) { ticking = true; requestAnimationFrame(update); }
  }, { passive: true });
  update();

  // current page marker
  const here = location.pathname.replace(/\/$/, '') || '/';
  document.querySelectorAll('.nav__link').forEach((a) => {
    const path = new URL(a.href).pathname.replace(/\/$/, '') || '/';
    if (path === here) a.setAttribute('aria-current', 'page');
  });
}

/* ————— Mobile menu ————— */
const menu = document.querySelector('[data-menu]');
const burger = document.querySelector('[data-menu-open]');
if (menu && burger) {
  const setOpen = (open) => {
    menu.classList.toggle('is-open', open);
    document.body.classList.toggle('menu-open', open);
    burger.setAttribute('aria-expanded', String(open));
    if (lenis) open ? lenis.stop() : lenis.start();
  };
  burger.addEventListener('click', () => setOpen(true));
  menu.querySelector('[data-menu-close]')?.addEventListener('click', () => setOpen(false));
  menu.querySelectorAll('a').forEach((a) => a.addEventListener('click', () => setOpen(false)));
  addEventListener('keydown', (e) => { if (e.key === 'Escape') setOpen(false); });
}

/* ————— Word splitter for blur-in headlines ————— */
function splitWords(el) {
  const walk = (node) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const frag = document.createDocumentFragment();
        child.textContent.split(/(\s+)/).forEach((piece) => {
          if (!piece) return;
          if (/^\s+$/.test(piece)) frag.appendChild(document.createTextNode(' '));
          else {
            const w = document.createElement('span');
            w.className = 'w';
            w.textContent = piece;
            frag.appendChild(w);
          }
        });
        child.replaceWith(frag);
      } else if (child.nodeType === Node.ELEMENT_NODE) walk(child);
    });
  };
  walk(el);
  return el.querySelectorAll('.w');
}

/* ————— Scroll choreography ————— */
if (hasGsap && !reduceMotion) {
  // blur-in headlines
  document.querySelectorAll('[data-blurtext]').forEach((el) => {
    const words = splitWords(el);
    gsap.to(words, {
      opacity: 1,
      filter: 'blur(0px)',
      y: 0,
      duration: 1.1,
      ease: 'power3.out',
      stagger: { each: 0.085, from: 'start' },
      scrollTrigger: { trigger: el, start: 'top 86%', once: true },
    });
  });

  // generic reveals
  document.querySelectorAll('[data-reveal]').forEach((el) => {
    gsap.to(el, {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      duration: 1.15,
      ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 88%', once: true },
    });
  });

  // image clip reveals
  document.querySelectorAll('[data-clip]').forEach((el) => {
    gsap.to(el, {
      clipPath: 'inset(0% 0% 0% 0% round 14px)',
      scale: 1,
      duration: 1.5,
      ease: 'power4.out',
      scrollTrigger: { trigger: el, start: 'top 85%', once: true },
    });
  });

  // counters
  document.querySelectorAll('[data-counter]').forEach((el) => {
    const end = parseFloat(el.dataset.counter);
    const suffix = el.dataset.suffix || '';
    const prefix = el.dataset.prefix || '';
    const obj = { v: 0 };
    gsap.to(obj, {
      v: end,
      duration: 1.8,
      ease: 'power2.out',
      snap: { v: 1 },
      scrollTrigger: { trigger: el, start: 'top 88%', once: true },
      onUpdate: () => { el.textContent = `${prefix}${Math.round(obj.v)}${suffix}`; },
    });
  });

  // background parallax
  document.querySelectorAll('[data-parallax]').forEach((img) => {
    const factor = parseFloat(img.dataset.parallax || '0.12');
    gsap.fromTo(
      img,
      { yPercent: -factor * 50 },
      {
        yPercent: factor * 50,
        ease: 'none',
        scrollTrigger: { trigger: img.closest('section') || img, start: 'top bottom', end: 'bottom top', scrub: true },
      }
    );
  });

  // gold foil sheen — scrubbed light sweep
  document.querySelectorAll('[data-foil]').forEach((el) => {
    gsap.fromTo(
      el,
      { backgroundPosition: '120% 0' },
      {
        backgroundPosition: '-20% 0',
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: 0.6 },
      }
    );
  });

  // ScrollTrigger positions are computed at creation, before fonts/images
  // settle and before Lenis emits its first scroll — so above-the-fold
  // triggers (the hero headline especially) can fail to fire. Refresh once
  // everything has laid out so any element already past its start plays.
  const refresh = () => ScrollTrigger.refresh();
  if (document.readyState === 'complete') refresh();
  else addEventListener('load', refresh, { once: true });
  document.fonts && document.fonts.ready.then(refresh);
  setTimeout(refresh, 400);

  // Safety net: any ABOVE-THE-FOLD reveal that a missed trigger left hidden
  // (or, in the case of blur-in words, opaque-but-still-blurred) gets forced
  // to its final shown state. Only in-view elements are touched, so genuine
  // below-fold reveals still wait for their scroll trigger. No clearProps —
  // the inline values must win over the CSS initial state.
  setTimeout(() => {
    const vh = innerHeight;
    const inView = (el) => { const r = el.getBoundingClientRect(); return r.top < vh * 0.95 && r.bottom > 0; };
    document.querySelectorAll('[data-reveal]').forEach((el) => {
      if (inView(el) && parseFloat(getComputedStyle(el).opacity) < 0.99) {
        gsap.set(el, { opacity: 1, x: 0, y: 0, scale: 1 });
      }
    });
    document.querySelectorAll('[data-blurtext]').forEach((h) => {
      if (!inView(h)) return;
      h.querySelectorAll('.w').forEach((w) => {
        const cs = getComputedStyle(w);
        const stillBlurred = cs.filter && cs.filter !== 'none' && !cs.filter.includes('blur(0');
        if (parseFloat(cs.opacity) < 0.99 || stillBlurred) {
          gsap.set(w, { opacity: 1, filter: 'blur(0px)', y: 0 });
        }
      });
    });
  }, 1400);

  // Below-fold rescue: a once:true trigger that never fires (stale positions
  // after late layout shifts, iframe embeds, missed Lenis updates) leaves its
  // element invisible forever while still holding layout height — a black
  // void. Watch every reveal element; once it truly enters the viewport, give
  // its tween time to finish (longest is ~1.9s with stagger), then force the
  // final shown state if it is still hidden.
  const forceShown = (el) => {
    if (el.hasAttribute('data-clip')) {
      const clip = getComputedStyle(el).clipPath;
      if (clip && clip !== 'none' && !/inset\(0%/.test(clip)) {
        gsap.set(el, { clipPath: 'inset(0% 0% 0% 0% round 14px)', scale: 1 });
      }
    } else if (el.hasAttribute('data-blurtext')) {
      el.querySelectorAll('.w').forEach((w) => {
        const cs = getComputedStyle(w);
        const stillBlurred = cs.filter && cs.filter !== 'none' && !cs.filter.includes('blur(0');
        if (parseFloat(cs.opacity) < 0.99 || stillBlurred) {
          gsap.set(w, { opacity: 1, filter: 'blur(0px)', y: 0 });
        }
      });
    } else if (parseFloat(getComputedStyle(el).opacity) < 0.99) {
      gsap.set(el, { opacity: 1, x: 0, y: 0, scale: 1 });
    }
  };
  const rescue = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      rescue.unobserve(entry.target);
      setTimeout(() => forceShown(entry.target), 2000);
    });
  }, { rootMargin: '0px 0px -10% 0px' });
  document.querySelectorAll('[data-reveal], [data-blurtext], [data-clip]').forEach((el) => rescue.observe(el));
}

/* The press marquee used to be paused here whenever `hasGsap && !reduceMotion` was
   false. That was too blunt: the marquee is a pure CSS animation that needs neither
   GSAP nor the scroll choreography, and `reduceMotion` folds in `flatMode` — which is
   true inside the Shopify theme editor (window.Shopify.designMode). The result was
   press logos frozen in the customiser, and frozen on any visit where the deferred
   GSAP bundle failed to load. The genuine accessibility case is already handled, and
   handled better, in motion.css:
     @media (prefers-reduced-motion: reduce) { .marquee__track { animation: none } }
   so honour the real media query there and leave the marquee alone here. */

/* ————— Marquee duplication (seamless loop needs 2 copies) ————— */
document.querySelectorAll('[data-marquee] .marquee__track').forEach((trackEl) => {
  trackEl.innerHTML += trackEl.innerHTML;
  trackEl.setAttribute('aria-hidden', 'false');
});

/* ————— Magnetic buttons ————— */
if (finePointer && !reduceMotion && hasGsap) {
  document.querySelectorAll('[data-magnetic]').forEach((btn) => {
    const strength = 14;
    btn.addEventListener('pointermove', (e) => {
      const r = btn.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width - 0.5) * strength;
      const y = ((e.clientY - r.top) / r.height - 0.5) * strength;
      gsap.to(btn, { x, y, duration: 0.4, ease: 'power2.out' });
    });
    btn.addEventListener('pointerleave', () => gsap.to(btn, { x: 0, y: 0, duration: 0.6, ease: 'elastic.out(1, 0.4)' }));
  });
}

/* ————— Card tilt ————— */
if (finePointer && !reduceMotion && hasGsap) {
  document.querySelectorAll('[data-tilt]').forEach((card) => {
    card.addEventListener('pointermove', (e) => {
      const r = card.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width;
      const py = (e.clientY - r.top) / r.height;
      const rx = (py - 0.5) * -4;
      const ry = (px - 0.5) * 4;
      gsap.to(card, { rotateX: rx, rotateY: ry, transformPerspective: 900, duration: 0.5, ease: 'power2.out' });
      card.style.setProperty('--mx', `${px * 100}%`);
      card.style.setProperty('--my', `${py * 100}%`);
    });
    card.addEventListener('pointerleave', () => gsap.to(card, { rotateX: 0, rotateY: 0, duration: 0.8, ease: 'power3.out' }));
  });
}

/* ————— Custom cursor ————— */
if (finePointer && !reduceMotion) {
  const cursor = document.querySelector('.cursor');
  if (cursor) {
    let cx = -100, cy = -100, tx = -100, ty = -100;
    addEventListener('pointermove', (e) => {
      tx = e.clientX; ty = e.clientY;
      document.body.classList.add('cursor-on');
    }, { passive: true });
    const loop = () => {
      cx += (tx - cx) * 0.22;
      cy += (ty - cy) * 0.22;
      cursor.style.transform = `translate(${cx}px, ${cy}px)`;
      requestAnimationFrame(loop);
    };
    loop();
    document.querySelectorAll('a, button, [data-tilt]').forEach((el) => {
      el.addEventListener('pointerenter', () => cursor.classList.add('is-active'));
      el.addEventListener('pointerleave', () => cursor.classList.remove('is-active'));
    });
  }
}

/* ————— Hero video: poster-first, lazy, respectful ————— */
const heroVideo = document.querySelector('[data-hero-video]');
if (heroVideo) {
  const conn = navigator.connection || {};
  const wantsVideo = !reduceMotion && conn.saveData !== true;
  if (wantsVideo) {
    const src = desktop ? heroVideo.dataset.srcDesktop : heroVideo.dataset.srcMobile;
    // Poster image is the always-on base layer; the video fades in only
    // once it is genuinely playing. If decode fails (or headless can't
    // play H.264), the poster simply stays — never a black rectangle.
    heroVideo.addEventListener('playing', () => heroVideo.classList.add('is-playing'), { once: true });
    const engage = () => {
      heroVideo.src = src;
      heroVideo.load();
      heroVideo.addEventListener('canplay', () => heroVideo.play().catch(() => {}), { once: true });
    };
    if (document.readyState === 'complete') engage();
    else addEventListener('load', engage, { once: true });
    // save cycles offscreen
    new IntersectionObserver(
      ([entry]) => { entry.isIntersecting ? heroVideo.play().catch(() => {}) : heroVideo.pause(); },
      { threshold: 0.05 }
    ).observe(heroVideo);
  } else {
    heroVideo.remove();
  }
}

/* ————— Botanical index: open the full twenty on request ————— */
const twentyWall = document.querySelector('[data-twenty-wall]');
const twentyToggle = document.querySelector('[data-twenty-toggle]');
if (twentyWall && twentyToggle) {
  twentyToggle.addEventListener('click', () => {
    const opening = twentyWall.classList.contains('is-collapsed');
    twentyWall.classList.toggle('is-collapsed', !opening);
    twentyToggle.setAttribute('aria-expanded', String(opening));
    twentyToggle.textContent = opening ? 'Show fewer' : 'Show all twenty botanicals';
    if (!opening) {
      // collapsing from far down the wall would strand the reader below the
      // fold — bring the section head back into view
      twentyWall.closest('.twenty').scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    }
  });
}

/* ————— Sticky mobile CTA ————— */
const sticky = document.querySelector('[data-sticky-cta]');
if (sticky) {
  const hero = document.querySelector('.hero, .page-hero');
  const buy = document.querySelector('.buy');
  const buybox = document.querySelector('.buybox');
  const realCtaOnScreen = () => {
    // home: hide as soon as the buy section starts to enter
    if (buy) return buy.getBoundingClientRect().top < innerHeight;
    // product: the buybox sits at the top, so hide only while it is genuinely
    // on screen — otherwise the sticky bar would never come back
    if (buybox) {
      const r = buybox.getBoundingClientRect();
      return r.bottom > 0 && r.top < innerHeight;
    }
    return false;
  };
  addEventListener('scroll', () => {
    const past = hero ? window.scrollY > hero.offsetHeight * 0.9 : window.scrollY > 600;
    sticky.classList.toggle('is-visible', past && !realCtaOnScreen());
  }, { passive: true });

  // On the product page the sticky button submits the real product form, so the
  // one-time / subscribe choice and the delivery interval carry through.
  const stickyAdd = sticky.querySelector('[data-sticky-add]');
  if (stickyAdd) {
    stickyAdd.addEventListener('click', () => {
      const form = document.querySelector('form[action*="/cart/add"]');
      if (!form) {
        location.href = '/products/timeless-elixir-facial-serum';
        return;
      }
      const submitter = form.querySelector('[name="add"]');
      if (form.requestSubmit) form.requestSubmit(submitter || undefined);
      else form.submit();
    });
  }
}

/* ————— WebGL layers (dynamic, gated) ————— */
const webglOK = (() => {
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') || c.getContext('webgl'));
  } catch { return false; }
})();

if (webglOK && !reduceMotion) {
  if (document.querySelector('[data-atmosphere]') && desktop) {
    import('webgl-atmosphere').then((m) => m.init()).catch(() => {});
  }
}
